import { useEffect, useCallback, useRef, useState } from 'react';
import { useCart, useCartCount, useCartItems } from '@backpackjs/storefront';
import { useRouter } from 'next/router';
import { nanoid } from 'nanoid';
import equal from 'fast-deep-equal';

import { mapLineItem } from './utils';

export function useDataLayerCart({
  baseEcommerce,
  DEBUG,
  userDataEvent,
  userDataEventTriggered,
  userProperties,
}) {
  const asPathRef = useRef(null);
  const cart = useCart();
  const cartCount = useCartCount();
  const cartItems = useCartItems();
  const router = useRouter();
  const asPath = router.asPath.split('?')[0];

  const [previousCartCount, setPreviousCartCount] = useState(null);
  const [previousCartItems, setPreviousCartItems] = useState(null);
  const [previousCartItemsMap, setPreviousCartItemsMap] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);

  const addToCartEvent = useCallback(
    ({
      cart: _cart,
      ecommerce,
      lineItems,
      userProperties: _userProperties,
    }) => {
      if (!lineItems?.length) return;
      const previousPath = sessionStorage.getItem('PREVIOUS_PATH');
      const list =
        (window.location.pathname.startsWith('/collections') &&
          window.location.pathname) ||
        (previousPath?.startsWith('/collections') && previousPath) ||
        '';
      const event = {
        event: 'dl_add_to_cart',
        event_id: nanoid(),
        event_time: new Date().toISOString(),
        user_properties: _userProperties,
        ecommerce: {
          ...ecommerce,
          cart_id: _cart.id?.split('/').pop(),
          cart_total: _cart.estimatedCost?.totalAmount?.amount,
          cart_count:
            _cart.lines?.reduce((acc, line) => acc + line.quantity, 0) || 0,
          add: {
            actionField: { list },
            products: lineItems.slice(0, 12).map(mapLineItem(list)),
          },
        },
      };

      window.dispatchEvent(
        new CustomEvent(event.event, {
          detail: event,
        })
      );
      if (DEBUG) console.log(`DataLayer:dispatch:${event.event}`, event);
    },
    []
  );

  const removeFromCartEvent = useCallback(
    ({
      cart: _cart,
      ecommerce,
      lineItems,
      userProperties: _userProperties,
    }) => {
      if (!lineItems?.length) return;
      const previousPath = sessionStorage.getItem('PREVIOUS_PATH');
      const list =
        (window.location.pathname.startsWith('/collections') &&
          window.location.pathname) ||
        (previousPath?.startsWith('/collections') && previousPath) ||
        '';
      const event = {
        event: 'dl_remove_from_cart',
        event_id: nanoid(),
        event_time: new Date().toISOString(),
        user_properties: _userProperties,
        ecommerce: {
          ...ecommerce,
          cart_id: _cart.id?.split('/').pop(),
          cart_total: _cart.estimatedCost?.totalAmount?.amount,
          cart_count:
            _cart.lines?.reduce((acc, line) => acc + line.quantity, 0) || 0,
          remove: {
            actionField: { list },
            products: lineItems.map(mapLineItem(list)),
          },
        },
      };

      window.dispatchEvent(
        new CustomEvent(event.event, {
          detail: event,
        })
      );
      if (DEBUG) console.log(`DataLayer:dispatch:${event.event}`, event);
    },
    []
  );

  const viewCartEvent = useCallback(
    ({ cart: _cart, ecommerce, userProperties: _userProperties }) => {
      if (!_cart) return;
      const previousPath = sessionStorage.getItem('PREVIOUS_PATH');
      const list =
        (window.location.pathname.startsWith('/collections') &&
          window.location.pathname) ||
        (previousPath?.startsWith('/collections') && previousPath) ||
        '';
      const event = {
        event: 'dl_view_cart',
        event_id: nanoid(),
        event_time: new Date().toISOString(),
        user_properties: _userProperties,
        ecommerce: {
          ...ecommerce,
          actionField: { list: 'cart' },
          cart_id: _cart.id?.split('/').pop(),
          cart_total: _cart.estimatedCost?.totalAmount?.amount,
          cart_count:
            _cart.lines?.reduce((acc, line) => acc + line.quantity, 0) || 0,
          products: _cart.lines?.map(mapLineItem(list)) || [],
        },
      };

      window.dispatchEvent(
        new CustomEvent(event.event, {
          detail: event,
        })
      );
      if (DEBUG) console.log(`DataLayer:dispatch:${event.event}`, event);
    },
    []
  );

  useEffect(() => {
    const viewSidecart = PubSub.subscribe(
      'VIEW_SIDECART',
      async (event, isOpen) => {
        setCartOpen(isOpen);
      }
    );
    return () => {
      if (viewSidecart) {
        PubSub.unsubscribe(viewSidecart);
      }
    };
  }, []);

  // Trigger 'dl_view_cart' event on cart page
  useEffect(() => {
    if (
      !asPath.startsWith('/cart') ||
      !cart ||
      !baseEcommerce ||
      !userProperties ||
      asPath === asPathRef.current
    )
      return undefined;
    userDataEvent({ ecommerce: baseEcommerce, userProperties });
    viewCartEvent({ cart, ecommerce: baseEcommerce, userProperties });
    asPathRef.current = asPath;
    return () => {
      asPathRef.current = null;
    };
  }, [asPath, !!cart, !!baseEcommerce, !!userProperties]);

  // Trigger 'dl_view_cart' event when cart is opened
  useEffect(() => {
    if (!baseEcommerce || !cartOpen || !userDataEventTriggered) return;
    viewCartEvent({ cart, ecommerce: baseEcommerce, userProperties });
  }, [!!baseEcommerce, cartOpen, userDataEventTriggered]);

  // Determine if a cart item was added, removed, or updated for events
  useEffect(() => {
    if (!cart || !baseEcommerce || !userDataEventTriggered) return;

    const cartItemsMap = cartItems.reduce((acc, line) => {
      if (!line.variant) return acc;
      const variantId = line.variant.id;
      if (!acc[variantId]) {
        return { ...acc, [variantId]: [line] };
      }
      return { ...acc, [variantId]: [...acc[variantId], line] };
    }, {});

    if (!previousCartItems || previousCartCount === cartCount) {
      setPreviousCartItems(cartItems);
      setPreviousCartCount(cartCount);
      setPreviousCartItemsMap(cartItemsMap);
      return;
    }

    const isAddedItems = [];
    const isIncreasedItems = [];
    const isRemovedItems = [];
    const isDecreasedItems = [];

    if (cartCount > previousCartCount) {
      cartItems.forEach((line, index) => {
        const variantId = line.variant?.id;
        if (!variantId) return;

        const previousLine = previousCartItemsMap[variantId]?.find(
          (prevLine) => {
            const hasSameSellingPlanSelection =
              (!prevLine.sellingPlanAllocation &&
                !line.sellingPlanAllocation) ||
              prevLine.sellingPlanAllocation?.sellingPlan?.id ===
                line.sellingPlanAllocation?.sellingPlan?.id;
            return (
              hasSameSellingPlanSelection &&
              equal(
                prevLine.attributes.filter((attr) => attr.key !== '_addedAt'),
                line.attributes.filter((attr) => attr.key !== '_addedAt')
              )
            );
          }
        );
        if (!previousLine) {
          isAddedItems.push({ ...line, index });
          return;
        }
        if (line.quantity > previousLine.quantity) {
          isIncreasedItems.push({
            ...line,
            quantity: line.quantity - previousLine.quantity,
            index,
          });
        }
      });
    } else if (cartCount < previousCartCount) {
      previousCartItems.forEach((prevLine, index) => {
        const variantId = prevLine.variant?.id;
        if (!variantId) return;

        const currentLine = cartItemsMap[variantId]?.find((line) => {
          const hasSameSellingPlanSelection =
            (!prevLine.sellingPlanAllocation && !line.sellingPlanAllocation) ||
            prevLine.sellingPlanAllocation?.sellingPlan?.id ===
              line.sellingPlanAllocation?.sellingPlan?.id;
          return (
            hasSameSellingPlanSelection &&
            equal(
              prevLine.attributes.filter((attr) => attr.key !== '_addedAt'),
              line.attributes.filter((attr) => attr.key !== '_addedAt')
            )
          );
        });
        if (!currentLine) {
          isRemovedItems.push({ ...prevLine, index });
          return;
        }
        if (currentLine.quantity < prevLine.quantity) {
          isDecreasedItems.push({
            ...prevLine,
            quantity: prevLine.quantity - currentLine.quantity,
            index,
          });
        }
      });
    }

    if (isAddedItems.length || isIncreasedItems.length) {
      [...isAddedItems, ...isIncreasedItems].forEach((line) => {
        addToCartEvent({
          cart,
          ecommerce: baseEcommerce,
          lineItems: [line],
          userProperties,
        });
      });
    }
    if (isRemovedItems.length || isDecreasedItems.length) {
      removeFromCartEvent({
        cart,
        ecommerce: baseEcommerce,
        lineItems: [...isRemovedItems, ...isDecreasedItems],
        userProperties,
      });
    }

    setPreviousCartItems(cartItems);
    setPreviousCartCount(cartCount);
    setPreviousCartItemsMap(cartItemsMap);
  }, [cart?.updatedAt, !!baseEcommerce, userDataEventTriggered]);
}
